import { resolveTrackGraphOptions } from './constants';
import type { BuildConvoyTrackGraphOptions } from './constants';
import { collapseSnapshotsToSegmentDrafts } from './collapse-segments';
import { buildMembershipSeries } from './membership-series';
import { normalizeTrackPointFacts } from './normalize-track-point-facts';
import { segmentVertices } from './segment-geometry';
import { assignSegmentColorIndices } from './assign-color-indices';
import type {
  ConvoyTrackSegment,
  DeviceTrackPoints,
} from './types';

export type { BuildConvoyTrackGraphOptions };

/**
 * Build temporal fork/merge track segments for **one shipment's** device tracks.
 * Same pipeline for 1…N devices — no single-device special case.
 */
export function buildConvoyTrackGraph(
  tracks: readonly DeviceTrackPoints[],
  options: BuildConvoyTrackGraphOptions = {},
): ConvoyTrackSegment[] {
  const opts = resolveTrackGraphOptions(options);
  const cleaned = tracks
    .map((t) => ({
      deviceId: t.deviceId,
      points: [...t.points]
        .filter(
          (p) =>
            Number.isFinite(p.lat) &&
            Number.isFinite(p.lng) &&
            Number.isFinite(p.timestampSec),
        )
        .map((p) => {
          const facts = normalizeTrackPointFacts(p);
          return {
            lat: p.lat,
            lng: p.lng,
            timestampSec: p.timestampSec,
            battery: facts.battery,
            fixTypeCode: facts.fixTypeCode,
            accuracyMeters: facts.accuracyMeters,
          };
        })
        .sort((a, b) => a.timestampSec - b.timestampSec),
    }))
    .filter((t) => t.deviceId > 0 && t.points.length > 0);

  if (cleaned.length === 0) return [];

  const snapshots = buildMembershipSeries(cleaned, opts);
  const drafts = collapseSnapshotsToSegmentDrafts(snapshots);

  const withGeometry: ConvoyTrackSegment[] = drafts
    .map((d, i) => {
      const vertices = segmentVertices(cleaned, drafts, d);
      return {
        ...d,
        id: `${d.groupKey}:${d.startSec}:${d.endSec}:${i}`,
        vertices,
        coordinates: vertices.map((v) => [v.lng, v.lat] as [number, number]),
        colorIndex: 0,
      };
    })
    .filter((s) => s.vertices.length >= 2);

  return assignSegmentColorIndices(withGeometry);
}
